import React, {
  useMemo,
  useEffect,
  useCallback,
  ReactNode,
  useState,
} from "react";
import { DotVerticalIcon, InformationIcon, MadIcon } from "../components/icons";

import { orderBy, compact } from "lodash";
import { useFieldArray, useForm } from "react-hook-form";
import { format } from "date-fns";

import { useHistory } from "react-router";

import useViewer from "../hooks/useViewer";

import useAddNotification from "../hooks/useAddNotification";
import useTranslations from "../hooks/useTranslations";
import useFillReport from "../graphql/hooks/mutations/useFillReport";
import useInsertReportUser from "../graphql/hooks/mutations/useInsertReportUser";

import {
  Button,
  Container,
  ScreenHeader,
  Link,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Modal,
  ModalActions,
  ModalBody,
  ModalHeader,
} from "../components/wombat-ui";

import TemplateQuestionComponent, {
  TemplateQuestionFormStatus,
} from "../components/reports/TemplateQuestion";
import { generatePdfDocument } from "../components/pdf/Report/ReportPDFBuilder";

import {
  ReportFragment,
  ReportMadFragment,
  TemplateQuestionFragment,
  CompanyMinimalFragment,
  UserSummaryFragment,
  TemplateFragment,
} from "../graphql/fragments";
import {
  DocusignEnvelopeStatus,
  ReportAnswer,
  ReportAnswerType,
  ReportAnswerVariables,
  TemplateQuestionType,
  UserRole,
} from "../graphql/types";
import useUpdateReport from "../graphql/hooks/mutations/useUpdateReport";
import { TemplateSectionFragment } from "../graphql/fragments/TemplateSection";
import TemplateSection from "../components/reports/TemplateSection";
import { GET_REPORT } from "../graphql/hooks/queries/useGetReport";
import ReportEmptyState from "../components/reports/ReportEmptyState";
import ReportSignatureModal from "../components/reports/ReportSignatureModal";

type SignatureStatusProps = {
  status: DocusignEnvelopeStatus;
  document: string | null;
};

function getAlertColorByStatus(status: DocusignEnvelopeStatus) {
  switch (status) {
    case "draft":
    case "sent":
    case "created":
    case "delivered":
    case "processing":
    case "signed":
      return "primary";
    case "declined":
    case "deleted":
    case "voided":
      return "error";
    case "completed":
      return "success";
  }
}

const SignatureStatus: React.FC<SignatureStatusProps> = ({
  status,
  document,
}) => {
  const t = useTranslations();

  if (!status) return null;

  const color = getAlertColorByStatus(status);
  return (
    <div className={`p-4 mb-6 rounded-md bg-${color}-100`}>
      <div className="flex">
        <div className="flex-shrink-0">
          <InformationIcon iconClassName={`h-5 w-5 text-${color}-400`} />
        </div>
        <div className="flex-1 ml-3 md:flex md:justify-between">
          <p className={`text-sm text-${color}-700`}>
            {t(`reports.signature.status.${status}` as const)}
          </p>
          {status === "completed" && document && (
            <p className="mt-3 text-sm md:mt-0 md:ml-6">
              <a
                href={document}
                className={`font-medium whitespace-nowrap text-${color}-700 hover:text-${color}-600`}
              >
                {t("reports.signature.downloadSignedReport")}
                <span aria-hidden="true" className="ml-1">&rarr;</span>
              </a>
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

function getUserRoleFromMad(mad: ReportMadFragment, id: string): UserRole {
  if (id === mad?.talent.id) return "talent";
  else if (mad?.referents.find(({ id: rId }) => rId === id)) return "referent";
  return "administrator";
}

type ReportScreenProps = {
  report: ReportFragment;
};

const ReportScreen: React.FC<ReportScreenProps> = ({ report }) => {
  const [addNotification] = useAddNotification();
  const t = useTranslations();
  const { id: userId, company, role: userRole } = useViewer();

  const [updateReport] = useUpdateReport({
    refetchQueries: [{ query: GET_REPORT, variables: { id: report.id } }],
  });
  const [fillReport] = useFillReport({
    refetchQueries: [{ query: GET_REPORT, variables: { id: report.id } }],
  });
  const [insertReportUser] = useInsertReportUser({
    refetchQueries: [{ query: GET_REPORT, variables: { id: report.id } }],
  });

  const role = getUserRoleFromMad(report?.mad, userId);

  const successNotification = useCallback(
    () =>
      addNotification({
        variables: {
          title: t("reports.notification.success") as string,
          status: "success",
        },
      }),
    []
  );
  const failureNotification = useCallback((error) => {
    let message = t("reports.notification.error");

    if (error.toString().match(/Expected value to not be null/)) {
      message = t("reports.notification.notNull");
    }

    addNotification({
      variables: {
        title: message as string,
        status: "error",
      },
    });
  }, []);

  const template = report?.template || report?.mad?.template;

  // Regroup all sections into one sorted array
  const templateSections: TemplateSectionFragment[] = useMemo(
    () =>
      orderBy(template?.templateSections, "order").map(
        ({ templateQuestions, ...rest }) => ({
          ...rest,
          templateQuestions: templateQuestions.filter((templateQuestion) => {
            // if the role is referent we only allow checkable questions
            if (!templateQuestion.reportTypes.includes(report.type)) {
              return false;
            }

            if (role === "talent") {
              return true;
            }

            if (
              role === "referent" &&
              templateQuestion.referents.map((r) => r.id).includes(userId)
            ) {
              return true;
            }

            if (userRole === "administrator") {
              return true;
            }

            return false;
          }),
        })
      ),
    [report, role, report.reportAnswers, template]
  );
  const _updateReport = useCallback(
    (answers: ReportAnswerVariables[]) =>
      // when we save we only send the answers that don't have an undefined/null value
      updateReport({
        variables: {
          input: {
            id: report.id,
            reportAnswersAttributes: answers,
          },
        },
      })
        .then(successNotification)
        .catch(failureNotification),
    [updateReport, report]
  );

  const _fillReport = useCallback(
    (answers: ReportAnswerVariables[]) => {
      if (confirm(t("reports.confirm") as string)) {
        return updateReport({
          variables: {
            input: {
              id: report.id,
              reportAnswersAttributes: answers,
            },
          },
        })
          .then(() =>
            fillReport({
              variables: {
                input: {
                  id: report.id,
                },
              },
            })
              .then(successNotification)
              .catch(failureNotification)
          )
          .catch(failureNotification);
      }
    },
    [fillReport, report]
  );

  const _insertReportUser = useCallback(
    (answers: ReportAnswerVariables[]) => {
      if (confirm(t("reports.confirm") as string)) {
        return updateReport({
          variables: {
            input: {
              id: report.id,
              reportAnswersAttributes: answers,
            },
          },
        })
          .then(() =>
            insertReportUser({
              variables: {
                input: {
                  reportId: report.id,
                  userId,
                  filledAt: new Date().toISOString(),
                },
              },
            })
              .then(successNotification)
              .catch(failureNotification)
          )
          .catch(failureNotification);
      }
    },
    [insertReportUser, report]
  );

  const referents = useMemo(
    () => report.reportReferents.map(({ referent }) => referent),
    [report.reportReferents]
  );

  return (
    <ReportScreenImpl
      sections={templateSections}
      report={report}
      template={template}
      role={role}
      onSubmit={(isDraft, answers) => {
        if (!isDraft && role === "talent") {
          _fillReport(answers);
        } else if (!isDraft && role === "referent") {
          _insertReportUser(answers);
        } else if (isDraft) {
          _updateReport(answers);
        }
      }}
      userId={userId}
      company={company}
      referents={referents}
    />
  );
};

function answerTypeFromQuestionType(
  type: TemplateQuestionType
): ReportAnswerType {
  switch (type) {
    case "TemplateQuestionTextShort":
      return "ReportAnswerTextShort";
    case "TemplateQuestionTextLong":
      return "ReportAnswerTextLong";
    case "TemplateQuestionSelectorSingle":
      return "ReportAnswerSelectorSingle";
    case "TemplateQuestionSelectorMultiple":
      return "ReportAnswerSelectorMultiple";
    case "TemplateQuestionSkill":
      return "ReportAnswerSkill";
    case "TemplateQuestionEvaluation":
      return "ReportAnswerEvaluation";
  }
}

function shouldBeDeserialized(type: ReportAnswerType): boolean {
  switch (type) {
    case "ReportAnswerTextShort":
    case "ReportAnswerTextLong":
      return false;
    case "ReportAnswerSelectorSingle":
    case "ReportAnswerSelectorMultiple":
    case "ReportAnswerSkill":
    case "ReportAnswerEvaluation":
      return true;
  }
}

type ReportScreenImplProps = {
  sections: TemplateSectionFragment[];
  report: ReportFragment;
  template: TemplateFragment;
  role: UserRole;
  userId: string;
  onSubmit: (isDraft: boolean, answers: ReportAnswerVariables[]) => void;
  company: CompanyMinimalFragment;
  referents: UserSummaryFragment[];
};

export type ReportFormInputs = {
  answers: ReportAnswerVariables[];
};

const ReportScreenImpl: React.FC<ReportScreenImplProps> = ({
  sections,
  report,
  template,
  role,
  userId,
  onSubmit,
  company,
  referents,
}) => {
  const t = useTranslations();

  const checkedAt = useMemo(
    () =>
      report.checkedAt ||
      report.reportReferents.find((rr) => rr.referent.id === userId)
        ?.filledAt ||
      undefined,
    [userId, report]
  );

  const {
    register,
    control,
    handleSubmit,
    setValue,
    errors,
  } = useForm<ReportFormInputs>({
    defaultValues: {
      answers: useMemo(() => {
        const defaultValues = sections.flatMap((section) =>
          section.templateQuestions.map(
            (templateQuestion): ReportAnswerVariables => {
              const reportAnswer = report.reportAnswers.find(
                (reportAnswer) =>
                  reportAnswer.user.id === userId &&
                  reportAnswer.templateQuestion.digest ===
                    templateQuestion.digest
              );

              return {
                id: reportAnswer?.id,
                templateQuestionId: templateQuestion.id,
                timeSpent: reportAnswer?.timeSpent,
                userId,
                type: answerTypeFromQuestionType(templateQuestion.type),
                value: {},
              };
            }
          )
        );
        return defaultValues;
      }, [report.reportAnswers, sections]),
    },
  });

  useEffect(() => {
    let i = 0;
    sections.flatMap((section) =>
      section.templateQuestions.map((q) => {
        setValue(
          `answers[${i++}].id`,
          report.reportAnswers.find(
            (a) =>
              a.user.id === userId && a.templateQuestion.digest === q.digest
          )?.id
        );
      })
    );
  }, [report.reportAnswers, sections]);

  const { fields } = useFieldArray({
    control,
    name: "answers",
    keyName: "_rhfid",
  });

  const _onSubmit = useCallback(
    ({ isDraft, data }: { isDraft: boolean; data: ReportFormInputs }) => {
      const parsedData = (data.answers || []).map((a) => {
        return {
          ...a,
          value: shouldBeDeserialized(a.type)
            ? (() => {
                try {
                  return JSON.parse(a.value);
                } catch (e) {
                  return a.value;
                }
              })()
            : a.value,
        };
      });
      onSubmit(isDraft, parsedData);
    },
    [onSubmit]
  );

  useEffect(() => {
    fields.forEach((f, index) => {
      register(`answers[${index}].id`);
      register(`answers[${index}].templateQuestionId`, { required: true });
      register(`answers[${index}].userId`, { required: true });
      register(`answers[${index}].type`, { required: true });

      setValue(`answers[${index}].id`, f.id);
      setValue(`answers[${index}].templateQuestionId`, f.templateQuestionId);
      setValue(`answers[${index}].userId`, f.userId);
      setValue(`answers[${index}].type`, f.type);
    });
  }, [fields]);

  const associatedAnswers: {
    [key: string]: {
      answers: ReportAnswer[];
      question: TemplateQuestionFragment;
    };
  } = useMemo(() => {
    const questions = sections.flatMap((section) => section.templateQuestions);

    return questions.reduce(
      (prev, q) => ({
        ...prev,
        [q.digest]: {
          answers: report.reportAnswers.filter(
            (a) => q.digest === a.templateQuestion.digest
          ),
          question: q,
        },
      }),
      {}
    );
  }, [sections, report.reportAnswers]);

  const templateQuestionFormStatus: TemplateQuestionFormStatus = useMemo(() => {
    if (role === "administrator") return "hr_review";
    if (role === "talent")
      return report.filledAt ? "talent_review" : "to_be_filled_talent";
    if (role === "referent")
      return checkedAt ? "evaluator_review" : "to_be_filled_evaluator";
  }, [role, report.filledAt, checkedAt]);

  const sudmittedAt = useMemo((): ReactNode | undefined => {
    if (report.filledAt && templateQuestionFormStatus === "talent_review")
      return t("reports.submittedAt", {
        date: format(new Date(report.filledAt), t("dateFormat") as string),
      });
    if (
      checkedAt &&
      (templateQuestionFormStatus === "evaluator_review" ||
        templateQuestionFormStatus === "hr_review")
    )
      return t("reports.submittedAt", {
        date: format(new Date(checkedAt), t("dateFormat") as string),
      });
  }, [report.filledAt, checkedAt, templateQuestionFormStatus]);

  const generatePdf = useCallback(
    async (
      pageSize: "A4" | "LETTER",
      directDownload: boolean,
      signatories: string[] = []
    ) => {
      return generatePdfDocument(
        associatedAnswers,
        report.mad,
        company,
        checkedAt,
        report.type,
        signatories,
        t,
        pageSize,
        directDownload
      );
    },
    [generatePdfDocument]
  );

  const firstUnansweredQuestion = useMemo(() => {
    const answeredTemplateQuestionDigests = report.reportAnswers
      .filter(({ isEmpty }) => !isEmpty)
      .map(({ templateQuestion }) => templateQuestion.digest);
    const templateQuestions = sections.flatMap(
      ({ templateQuestions }) => templateQuestions
    );

    return templateQuestions.find(
      ({ digest }) => !answeredTemplateQuestionDigests.includes(digest)
    );
  }, [report.reportAnswers, sections]);

  const sectionsFields = useMemo(() => {
    let displayedSectionIndex = 0;

    return sections.map((section) => {
      if (section.templateQuestions.length === 0) {
        return null;
      }

      displayedSectionIndex++;

      return (
        <TemplateSection
          section={section}
          displayedIndex={`${displayedSectionIndex}`}
          key={section.id}
          defaultOpen={
            !!section.templateQuestions.find(
              ({ digest }) => digest === firstUnansweredQuestion?.digest
            )
          }
        >
          {section.templateQuestions.map((templateQuestion, index) => {
            const fieldIndex = fields.findIndex(
              (f) => templateQuestion.id === f.templateQuestionId
            );
            const name = `answers[${fieldIndex}]`;

            return (
              <TemplateQuestionComponent
                key={templateQuestion.id}
                question={associatedAnswers[templateQuestion.digest].question}
                mad={report.mad}
                answers={associatedAnswers[templateQuestion.digest].answers}
                control={control}
                register={register}
                setValue={setValue}
                role={role}
                status={templateQuestionFormStatus}
                errors={errors?.answers?.[fieldIndex]}
                name={name}
                autoFocus={
                  templateQuestion.digest === firstUnansweredQuestion?.digest
                }
                displayIndex={
                  section.type === "TemplateSectionBlank"
                    ? `${displayedSectionIndex}`
                    : `${displayedSectionIndex}.${index + 1}`
                }
              />
            );
          })}
        </TemplateSection>
      );
    });
  }, [fields, referents, sections, firstUnansweredQuestion]);

  const handleSubmitReport = handleSubmit((data) =>
    _onSubmit({ isDraft: false, data })
  );
  const handleSaveReport = handleSubmit((data) =>
    _onSubmit({ isDraft: true, data })
  );

  const [
    updateTemplateWarningModalOpen,
    setUpdateTemplateWarningModalOpen,
  ] = useState(false);

  const handleOpenTemplateWarningModal = useCallback((e) => {
    e.preventDefault();
    setUpdateTemplateWarningModalOpen(true);
  }, []);

  const handleCloseTemplateWarningModal = useCallback(() => {
    setUpdateTemplateWarningModalOpen(false);
  }, []);

  const editTemplateUrl = `/templates/${report.mad.templateId}/edit?reportId=${report.id}&madId=${report.mad.id}`;
  const history = useHistory();

  const handleGoToTemplate = useCallback(() => {
    history.push(editTemplateUrl);
  }, [history]);

  const handleSaveAndGoToTemplate = useCallback(() => {
    handleSaveReport().then(handleGoToTemplate);
  }, [handleSaveReport, handleGoToTemplate]);

  const [signModalOpen, setSignModalOpen] = useState(false);
  const handleOpenSignModal = useCallback(() => {
    setSignModalOpen(true);
  }, []);
  const handleCloseSignModal = useCallback(() => {
    setSignModalOpen(false);
  }, []);

  const possibleSignatories: (UserSummaryFragment & {
    madRole: "talent" | "referent" | "supervisor";
  })[] = useMemo(
    () =>
      compact([
        { ...report.mad.talent, madRole: "talent" },
        ...report.mad.madSupervisors.map(({ user }) => ({
          ...user,
          madRole: "supervisor",
        })),
        ...report.reportReferents.map(({ referent }) => ({
          ...referent,
          madRole: "referent",
        })),
      ]) as (UserSummaryFragment & {
        madRole: "talent" | "referent" | "supervisor";
      })[],
    [report]
  );

  const contextMenu = (
    <Menu key="reportContextMenu">
      <MenuButton
        id="generatePDFMenuTrigger"
        icon={DotVerticalIcon}
        variant="ghost"
      />
      <MenuList>
        <MenuItem
          disabled={!checkedAt}
          onSelect={() => generatePdf("A4", true)}
          data-test-id="reportExportPDFA4"
        >
          {t("pdf.downloadAsPDF")} - A4
        </MenuItem>
        <MenuItem
          disabled={!checkedAt}
          onSelect={() => generatePdf("LETTER", true)}
          data-test-id="reportExportPDFLetter"
        >
          {t("pdf.downloadAsPDF")} - letter
        </MenuItem>
      </MenuList>
    </Menu>
  );

  const reportsHasNoQuestions = useMemo(() => (
    sections.every(({ templateQuestions }) => templateQuestions.length === 0)
  ), [sections]);

  return (
    <>
      <ScreenHeader
        title={report.mad.talent.name}
        icon={MadIcon}
        description={`${template.title}${
          sudmittedAt ? " - " + sudmittedAt : ""
        }`}
        actions={
          templateQuestionFormStatus === "to_be_filled_evaluator" ||
          templateQuestionFormStatus === "to_be_filled_talent"
            ? [
                <Button
                  id="save_button"
                  key="saveButton"
                  variant="subtle"
                  disabled={
                    templateQuestionFormStatus === "to_be_filled_talent" &&
                    role === "referent"
                  }
                  onClick={handleSaveReport}
                >
                  {t("reports.save")}
                </Button>,
                <Button
                  id="submit_button"
                  key="submitButton"
                  form="reportForm"
                  type="submit"
                  disabled={
                    templateQuestionFormStatus === "to_be_filled_talent" &&
                    role === "referent"
                  }
                  onClick={handleSubmitReport}
                >
                  {t("reports.submit")}
                </Button>,
                contextMenu,
              ]
            : report.canSign && report.docusignEnvelopeStatus === null
            ? [
                <Button
                  id="sign_button"
                  key="signButton"
                  variant="subtle"
                  onClick={handleOpenSignModal}
                >
                  {t("reports.sign")}
                </Button>,
                contextMenu,
              ]
            : [contextMenu]
        }
      />
      <Container className="flex flex-col">
        {report.docusignEnvelopeStatus !== null && (
          <SignatureStatus
            status={report.docusignEnvelopeStatus}
            document={report.document}
          />
        )}
        <form
          onSubmit={handleSubmitReport}
          className="flex flex-col pb-12 space-y-10"
          id="reportForm"
        >
          {sectionsFields}
          {template.canInsert && report.canUpdate && (
            <>
              {reportsHasNoQuestions && <ReportEmptyState />}
              <div className="flex justify-center">
                <Link
                  to={editTemplateUrl}
                  variant="button-subtle"
                  onClick={handleOpenTemplateWarningModal}
                  data-test-id="updateTemplateLink"
                >
                  {t("reports.updateTemplate")}
                </Link>
              </div>
            </>
          )}
        </form>
      </Container>
      <Modal
        isOpen={updateTemplateWarningModalOpen}
        aria-label="Save your work"
        id={`updateTemplateWarningModal`}
      >
        <ModalHeader
          onClose={handleCloseTemplateWarningModal}
          title={t("reports.updateTemplate") as string}
        >
          {t("reports.updateTemplate")}
        </ModalHeader>
        <ModalBody>{t("reports.updateTemplateWarning")}</ModalBody>
        <ModalActions>
          <Button
            variant="subtle"
            onClick={handleGoToTemplate}
            id={`updateTemplateWithoutSavingButton`}
          >
            {t("reports.updateTemplateWithoutSavingButtonLabel")}
          </Button>
          <Button
            variant="primary"
            onClick={handleSaveAndGoToTemplate}
            id={`updateTemplateWithSavingButton`}
          >
            {t("reports.updateTemplateWithSavingButtonLabel")}
          </Button>
        </ModalActions>
      </Modal>

      <ReportSignatureModal
        reportId={report.id}
        generateDocument={generatePdf}
        open={signModalOpen}
        onClose={handleCloseSignModal}
        signatories={possibleSignatories}
      />
    </>
  );
};

export default ReportScreen;
